import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, ArrowLeft, Building2, Globe, Palette, Target, Users, MessageCircle, Zap, Send } from 'lucide-react';
import { cjmsApi } from '../api/cjmsApi';

interface QuestionnaireProps {
    leadId: string;
    email: string;
    onComplete?: (responses: Record<string, any>) => void;
    onCancel?: () => void;
}

const SECTIONS = [
    {
        id: 'business_basics',
        title: 'Business Basics',
        icon: Building2,
        description: 'Tell us about your business',
        fields: [
            { id: 'business_name', label: 'Business Name', type: 'text', placeholder: 'e.g. ABC Plumbing', required: true },
            { id: 'business_type', label: 'Industry', type: 'select', options: ['Construction', 'Plumbing', 'Electrical', 'Cleaning', 'Landscaping', 'HVAC', 'Automotive', 'Beauty', 'Fitness', 'Catering', 'Events', 'Consulting', 'Real Estate', 'Photography', 'IT', 'Education', 'Healthcare', 'Legal', 'Accounting', 'Other'], required: true },
            { id: 'business_description', label: 'What does your business do? (1-2 sentences)', type: 'textarea', placeholder: 'e.g. We provide residential and commercial plumbing services in Cape Town', required: true },
            { id: 'years_in_business', label: 'How long have you been in business?', type: 'select', options: ['Less than 1 year', '1-2 years', '3-5 years', '5-10 years', '10+ years'], required: true },
        ],
    },
    {
        id: 'your_goals',
        title: 'Your Goals',
        icon: Target,
        description: 'What do you want to achieve?',
        fields: [
            { id: 'primary_goal', label: 'What is your main goal right now?', type: 'select', options: ['Find my first customers', 'Get to 10 customers', 'Scale to 100 customers', 'Build a team', 'Automate my business'], required: true },
            { id: 'monthly_revenue', label: 'What is your current monthly revenue? (Estimate)', type: 'select', options: ['R0 - Just starting', 'R1 - R5,000', 'R5,000 - R20,000', 'R20,000 - R50,000', 'R50,000 - R100,000', 'R100,000+'], required: true },
            { id: 'freedom_number', label: 'What monthly revenue would give you freedom?', type: 'text', placeholder: 'e.g. R50,000', required: true },
            { id: 'biggest_challenge', label: 'What is your biggest challenge right now?', type: 'select', options: ['Getting leads', 'Converting leads to customers', 'Delivering consistently', 'Managing operations', 'Hiring staff', 'Scaling revenue', 'Marketing', 'Cash flow'], required: true },
        ],
    },
    {
        id: 'website_needs',
        title: 'Website Needs',
        icon: Globe,
        description: 'What should your website achieve?',
        fields: [
            { id: 'website_purpose', label: 'What is the primary purpose of your website?', type: 'select', options: ['Generate leads', 'Showcase my work', 'Sell products/services', 'Book appointments', 'All of the above'], required: true },
            { id: 'key_services', label: 'List your top 3 services/products', type: 'textarea', placeholder: 'e.g. 1. Emergency plumbing\n2. Bathroom renovations\n3. Drain cleaning', required: true },
            { id: 'target_audience', label: 'Who is your ideal customer?', type: 'text', placeholder: 'e.g. Homeowners in Cape Town aged 25-55', required: true },
            { id: 'unique_selling_point', label: 'What makes you different from competitors?', type: 'textarea', placeholder: 'e.g. 24/7 emergency service, free quotes, 10+ years experience', required: true },
        ],
    },
    {
        id: 'design_brand',
        title: 'Design & Brand',
        icon: Palette,
        description: 'How should your website look and feel?',
        fields: [
            { id: 'color_preferences', label: 'Any color preferences?', type: 'text', placeholder: 'e.g. Blue and white, or "professional and clean"', required: false },
            { id: 'brand_personality', label: 'Describe your brand personality', type: 'select', options: ['Professional & Trustworthy', 'Friendly & Approachable', 'Bold & Dynamic', 'Luxurious & Premium', 'Minimalist & Clean'], required: true },
            { id: 'websites_you_like', label: 'Share 1-2 websites you like (URLs)', type: 'textarea', placeholder: 'e.g. https://example-plumber.co.za', required: false },
            { id: 'content_tone', label: 'What tone should your content have?', type: 'select', options: ['Professional', 'Friendly', 'Authoritative', 'Casual', 'Inspirational'], required: true },
        ],
    },
    {
        id: 'contact_info',
        title: 'Contact & Go-Live',
        icon: MessageCircle,
        description: 'How can customers reach you?',
        fields: [
            { id: 'phone', label: 'Business phone number', type: 'text', placeholder: 'e.g. +27 81 123 4567', required: true },
            { id: 'email', label: 'Business email', type: 'text', placeholder: 'e.g. info@mybusiness.co.za', required: true },
            { id: 'address', label: 'Business address (optional)', type: 'text', placeholder: 'e.g. Cape Town, South Africa', required: false },
            { id: 'service_areas', label: 'Areas you serve', type: 'text', placeholder: 'e.g. Cape Town, Stellenbosch, Paarl', required: true },
            { id: 'cta_preference', label: 'What action should visitors take?', type: 'select', options: ['Call us', 'Request a quote', 'Book an appointment', 'Send a message', 'View our services'], required: true },
        ],
    },
];

export const Questionnaire: React.FC<QuestionnaireProps> = ({
    leadId,
    email,
    onComplete,
    onCancel,
}) => {
    const [currentSection, setCurrentSection] = useState(0);
    const [responses, setResponses] = useState<Record<string, any>>({});
    const [step, setStep] = useState<'filling' | 'submitting' | 'success' | 'error'>('filling');
    const [errorMsg, setErrorMsg] = useState('');

    const totalFields = SECTIONS.reduce((sum, s) => sum + s.fields.filter(f => f.required).length, 0);
    const answeredRequired = SECTIONS.reduce((sum, s) => {
        return sum + s.fields.filter(f => f.required && responses[f.id]).length;
    }, 0);
    const progressPercent = Math.round((answeredRequired / totalFields) * 100);

    const handleFieldChange = (fieldId: string, value: any) => {
        setResponses(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleNext = () => {
        if (currentSection < SECTIONS.length - 1) {
            setCurrentSection(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (currentSection > 0) {
            setCurrentSection(prev => prev - 1);
        }
    };

    const handleSubmit = async () => {
        // Validate required fields
        const missing = SECTIONS.flatMap(s => s.fields.filter(f => f.required && !responses[f.id]));
        if (missing.length > 0) {
            setErrorMsg(`Please fill in: ${missing.map(f => f.label).join(', ')}`);
            setStep('error');
            return;
        }

        setStep('submitting');
        try {
            const result = await cjmsApi.submitQuestionnaire({
                leadId,
                email,
                responses,
            });
            if (result.success) {
                setStep('success');
                onComplete?.(responses);
            } else {
                setStep('error');
                setErrorMsg(result.error || 'Failed to submit questionnaire');
            }
        } catch {
            setStep('error');
            setErrorMsg('Network error — please try again');
        }
    };

    if (step === 'submitting') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                    <h2 className="text-lg font-bold text-slate-800 mb-2">Submitting Your Questionnaire</h2>
                    <p className="text-xs text-slate-500">We're setting up your website generation...</p>
                </div>
            </motion.div>
        );
    }

    if (step === 'success') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-800 mb-2">Questionnaire Submitted!</h2>
                    <p className="text-xs text-slate-500 mb-4">
                        We'll generate your website within 1-2 minutes. You'll receive a preview link at <strong>{email}</strong>.
                    </p>
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
                        <div className="flex items-center gap-2 justify-center mb-2">
                            <MessageCircle className="w-4 h-4 text-blue-600" />
                            <p className="text-xs text-blue-700 font-medium">Questions Answered</p>
                        </div>
                        <p className="text-[10px] text-slate-600">
                            Need help? WhatsApp us at +27 81 714 2228 or use the in-app chat. Our support team responds within 6-12 hours.
                        </p>
                    </div>
                    {onComplete && (
                        <button onClick={() => onComplete(responses)} className="w-full px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                            Continue
                        </button>
                    )}
                </div>
            </motion.div>
        );
    }

    if (step === 'error') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <p className="text-sm text-red-600 font-medium mb-4">{errorMsg}</p>
                    <button onClick={() => setStep('filling')} className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        Try Again
                    </button>
                </div>
            </motion.div>
        );
    }

    const section = SECTIONS[currentSection];
    const Icon = section.icon;
    const isFirst = currentSection === 0;
    const isLast = currentSection === SECTIONS.length - 1;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto p-6">
            {/* Progress bar */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-slate-500 font-medium">Progress</span>
                    <span className="text-[10px] text-blue-600 font-bold">{progressPercent}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercent}%` }}
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                    />
                </div>
                <div className="flex justify-between mt-2">
                    {SECTIONS.map((s, i) => (
                        <div key={s.id} className={`w-2 h-2 rounded-full ${i <= currentSection ? 'bg-blue-500' : 'bg-slate-200'}`} />
                    ))}
                </div>
            </div>

            {/* Section header */}
            <div className="text-center mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-800">{section.title}</h2>
                <p className="text-xs text-slate-500 mt-1">{section.description}</p>
            </div>

            {/* Fields */}
            <div className="space-y-4 mb-6">
                {section.fields.map(field => (
                    <div key={field.id}>
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.type === 'text' && (
                            <input
                                type="text"
                                value={responses[field.id] || ''}
                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                        )}
                        {field.type === 'textarea' && (
                            <textarea
                                value={responses[field.id] || ''}
                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                placeholder={field.placeholder}
                                rows={3}
                                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200 resize-none"
                            />
                        )}
                        {field.type === 'select' && (
                            <select
                                value={responses[field.id] || ''}
                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white"
                            >
                                <option value="">Select...</option>
                                {field.options?.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        )}
                    </div>
                ))}
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
                {!isFirst && (
                    <button onClick={handleBack} className="flex-1 px-4 py-2 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors flex items-center justify-center gap-1">
                        <ArrowLeft className="w-3 h-3" />
                        Back
                    </button>
                )}
                {isLast ? (
                    <button onClick={handleSubmit} className="flex-1 px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1">
                        <Send className="w-3 h-3" />
                        Submit Questionnaire
                    </button>
                ) : (
                    <button onClick={handleNext} className="flex-1 px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1">
                        Next
                        <ArrowRight className="w-3 h-3" />
                    </button>
                )}
            </div>

            {onCancel && (
                <div className="text-center mt-4">
                    <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-600 underline">
                        Back to dashboard
                    </button>
                </div>
            )}
        </motion.div>
    );
};
